import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useStorage } from '../../hooks/useStorage';
import CommonPageHeader from '../common/CommonPageHeader';
import {
  ChartPieIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FireIcon,
  CloudIcon,
  ArchiveBoxIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Squares2X2Icon as SnowflakeIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

interface FileInfo {
  name: string;
  path: string;
  size: number;
  created: Date;
  ageInDays: number;
  tier: 'hot' | 'warm' | 'cold' | 'archive';
}

interface StorageDashboardData {
  analytics: {
    totalFiles: number;
    totalSize: number;
    hotFiles: number;
    warmFiles: number;
    coldFiles: number;
    archiveFiles: number;
    potentialSavings: number;
    recommendedActions: string[];
    filesList: FileInfo[];
  };
  costs: {
    current: number;
    optimized: number;
    savings: number;
    savingsPercentage: number;
  };
  lifecycleActions: string[];
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 6,
    maximumFractionDigits: 6
  }).format(amount);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

interface StorageOptimizationDashboardProps {
  showHeader?: boolean;
}

export const StorageOptimizationDashboard: React.FC<StorageOptimizationDashboardProps> = ({ 
  showHeader = true 
}) => {
  const { user } = useAuth();
  const { getCostOptimizationData, applyLifecycleRules, moveFileToTier } = useStorage();
  const [data, setData] = useState<StorageDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [movingFile, setMovingFile] = useState<string | null>(null);
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadDashboardData();
  }, [user?.organizationId]);
  
  // 🔒 Only sys admins can see this dashboard
  if (user?.role !== 'super_admin') {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Hozzáférés megtagadva</h3>
        <p className="text-gray-600">
          Ez a funkció csak rendszergazdák számára érhető el.
        </p>
      </div>
    );
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading storage optimization data...');
      
      // Valós adatok lekérése a useStorage hook-ból (enableLogging = true for admin)
      const storageAnalytics = await getCostOptimizationData(user?.organizationId, true);
      
      setData({
        analytics: {
          totalFiles: storageAnalytics.totalFiles,
          totalSize: storageAnalytics.totalSize,
          hotFiles: storageAnalytics.hotFiles,
          warmFiles: storageAnalytics.warmFiles,
          coldFiles: storageAnalytics.coldFiles,
          archiveFiles: storageAnalytics.archiveFiles,
          potentialSavings: storageAnalytics.potentialSavings,
          recommendedActions: storageAnalytics.recommendedActions,
          filesList: storageAnalytics.filesList
        },
        costs: storageAnalytics.costs,
        lifecycleActions: []
      });
      
      console.log('✅ Storage optimization data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      
      // Fallback to empty data
      setData({
        analytics: {
          totalFiles: 0,
          totalSize: 0,
          hotFiles: 0,
          warmFiles: 0,
          coldFiles: 0,
          archiveFiles: 0,
          potentialSavings: 0,
          recommendedActions: ['Nem sikerült betölteni az adatokat'],
          filesList: []
        },
        costs: {
          current: 0,
          optimized: 0,
          savings: 0,
          savingsPercentage: 0
        },
        lifecycleActions: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOptimize = async (dryRun: boolean = false) => {
    try {
      setOptimizing(true);
      console.log(`🔧 ${dryRun ? 'Analyzing' : 'Optimizing'} storage...`);
      
      // Valós lifecycle rules alkalmazása
      const actions = await applyLifecycleRules(user?.organizationId, dryRun);
      
      if (dryRun) {
        console.log('📊 Analysis complete:', actions);
        alert(`Elemzés kész:\n\n${actions.join('\n')}`);
      } else {
        console.log('🔧 Optimization complete:', actions);
        alert(`Optimalizálás kész:\n\n${actions.join('\n')}`);
        // Refresh data after optimization
        await loadDashboardData();
      }
    } catch (error) {
      console.error('❌ Error optimizing storage:', error);
      alert('Hiba történt a tárolás optimalizálása során. Lásd a konzolt a részletekért.');
    } finally {
      setOptimizing(false);
    }
  };

  const handleMoveFile = (filePath: string, targetTier: 'hot' | 'warm' | 'cold' | 'archive') => {
    const tierNames = {
      hot: 'Hot (Standard)',
      warm: 'Warm (Nearline)',
      cold: 'Cold (Coldline)',
      archive: 'Archive'
    };

    const fileName = filePath.split('/').pop() || filePath;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Fájl áthelyezése',
      message: `Biztosan át szeretnéd helyezni ezt a fájlt a ${tierNames[targetTier]} tárolásba?\n\nFájl: ${fileName}\n\nEz megváltoztatja a fájl tárolási osztályát és befolyásolhatja a költségeket.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        
        try {
          setMovingFile(filePath);
          const success = await moveFileToTier(filePath, targetTier);
          
          if (success) {
            setAlertDialog({
              isOpen: true,
              title: 'Sikeres áthelyezés',
              message: `A fájl sikeresen áthelyezve: ${tierNames[targetTier]}\n\nA fájl mostantól ${tierNames[targetTier]} tárolási osztályban van.`,
              type: 'success'
            });
            // Refresh data
            await loadDashboardData();
          } else {
            setAlertDialog({
              isOpen: true,
              title: 'Hiba',
              message: 'Hiba történt a fájl áthelyezése során.',
              type: 'error'
            });
          }
        } catch (error) {
          console.error('Error moving file:', error);
          setAlertDialog({
            isOpen: true,
            title: 'Hiba',
            message: 'Hiba történt a fájl áthelyezése során.',
            type: 'error'
          });
        } finally {
          setMovingFile(null);
        }
      }
    });
  };

  const handleBulkMove = (files: FileInfo[], targetTier: 'hot' | 'warm' | 'cold' | 'archive') => {
    const tierNames = {
      hot: 'Hot (Standard)',
      warm: 'Warm (Nearline)',
      cold: 'Cold (Coldline)',
      archive: 'Archive'
    };

    if (files.length === 0) return;

    setConfirmDialog({
      isOpen: true,
      title: 'Összes fájl áthelyezése',
      message: `Biztosan át szeretnéd helyezni mind a ${files.length} fájlt a ${tierNames[targetTier]} tárolásba?\n\nEz megváltoztatja az összes fájl tárolási osztályát és jelentősen befolyásolhatja a költségeket.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        
        let successCount = 0;
        let failCount = 0;

        for (const file of files) {
          try {
            setMovingFile(file.path);
            const success = await moveFileToTier(file.path, targetTier);
            if (success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error('Error moving file:', file.path, error);
            failCount++;
          }
        }

        setMovingFile(null);

        if (failCount === 0) {
          setAlertDialog({
            isOpen: true,
            title: 'Sikeres tömeges áthelyezés',
            message: `Mind a ${successCount} fájl sikeresen áthelyezve a ${tierNames[targetTier]} tárolásba.`,
            type: 'success'
          });
        } else {
          setAlertDialog({
            isOpen: true,
            title: 'Részleges siker',
            message: `${successCount} fájl sikeresen áthelyezve, ${failCount} fájl áthelyezése sikertelen.`,
            type: failCount > successCount ? 'error' : 'success'
          });
        }

        // Refresh data
        await loadDashboardData();
      }
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
        <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Nem sikerült betölteni az adatokat</p>
      </div>
    );
  }

  const { analytics, costs, lifecycleActions } = data;

  return (
    <div className="space-y-6">
      {/* Common Page Header */}
      {showHeader && (
        <CommonPageHeader
          title="Storage Optimization Dashboard"
          subtitle="Intelligens tárolás kezelés és költség optimalizálás"
          icon={ChartPieIcon}
          actionButton={
            <div className="flex space-x-3">
            <button
              onClick={() => handleOptimize(true)}
              disabled={optimizing}
              className="px-4 py-2 text-sm font-medium text-white bg-white/20 backdrop-blur-sm border border-white/30 rounded-md hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              {optimizing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button
              onClick={() => handleOptimize(false)}
              disabled={optimizing}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              {optimizing ? 'Optimizing...' : 'Optimize Now'}
            </button>
          </div>
        }
        />
      )}

      {/* Cost Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
            <div className="flex items-center">
              <CurrencyDollarIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Jelenlegi havi költség</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100 font-mono">{formatCurrency(costs.current)}</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">Standard Storage @ $0.026/GB</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center">
              <CloudArrowDownIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Optimalizált költség</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100 font-mono">{formatCurrency(costs.optimized)}</p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">Multi-tier tárolás</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Potenciális megtakarítás</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 font-mono">
                  {formatCurrency(costs.savings)}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{costs.savingsPercentage.toFixed(2)}% megtakarítás</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Cost Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Részletes Költség Lebontás</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Storage Tier
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fájlok
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Becsült Méret (GB)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ár/GB/hó
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Havi Költség
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <FireIcon className="w-4 h-4 text-red-600 mr-2" />
                    Hot (Standard)
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {analytics.hotFiles}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {((analytics.hotFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024))).toFixed(6)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  $0.026000
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono font-semibold">
                  {formatCurrency((analytics.hotFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024)) * 0.026)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <CloudIcon className="w-4 h-4 text-orange-600 mr-2" />
                    Warm (Nearline)
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {analytics.warmFiles}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {((analytics.warmFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024))).toFixed(6)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  $0.010000
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono font-semibold">
                  {formatCurrency((analytics.warmFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024)) * 0.010)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <SnowflakeIcon className="w-4 h-4 text-blue-600 mr-2" />
                    Cold (Coldline)
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {analytics.coldFiles}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {((analytics.coldFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024))).toFixed(6)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  $0.004000
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono font-semibold">
                  {formatCurrency((analytics.coldFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024)) * 0.004)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <ArchiveBoxIcon className="w-4 h-4 text-gray-600 mr-2" />
                    Archive
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {analytics.archiveFiles}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  {((analytics.archiveFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024))).toFixed(6)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400 font-mono">
                  $0.001200
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono font-semibold">
                  {formatCurrency((analytics.archiveFiles / analytics.totalFiles) * (analytics.totalSize / (1024 * 1024 * 1024)) * 0.0012)}
                </td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  Összesen (Optimalizált)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono">
                  {analytics.totalFiles}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono">
                  {(analytics.totalSize / (1024 * 1024 * 1024)).toFixed(6)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500 dark:text-gray-400">
                  -
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 font-mono">
                  {formatCurrency(costs.optimized)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Storage Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Fájl Eloszlás</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FireIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Hot Storage</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{analytics.hotFiles} files</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Gyakran használt</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CloudIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Warm Storage</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{analytics.warmFiles} files</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Rendszeresen használt</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <SnowflakeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Cold Storage</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{analytics.coldFiles} files</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Ritkán használt</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ArchiveBoxIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Archive</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{analytics.archiveFiles} files</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Archívum</div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-900 dark:text-gray-100">Összesen:</span>
              <div className="text-right">
                <div className="text-gray-900 dark:text-gray-100">{analytics.totalFiles} files</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(analytics.totalSize)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Javaslatok</h3>
          {analytics.recommendedActions.length > 0 ? (
            <div className="space-y-3">
              {analytics.recommendedActions.map((action, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{action}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-12 h-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">Minden fájl optimálisan van tárolva!</p>
            </div>
          )}
        </div>
      </div>

      {/* Files List by Tier */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Fájlok Tier Szerint</h3>
        <div className="space-y-6">
          {/* Warm Files (candidates for optimization) */}
          {analytics.filesList.filter(f => f.tier === 'warm').length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-orange-600 dark:text-orange-400 flex items-center">
                  <CloudIcon className="w-4 h-4 mr-2" />
                  Warm Storage ({analytics.filesList.filter(f => f.tier === 'warm').length} fájl)
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkMove(analytics.filesList.filter(f => f.tier === 'warm'), 'cold')}
                    className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded flex items-center gap-1"
                    title="Összes áthelyezése Cold-ba"
                  >
                    ❄️ Összes → Cold
                  </button>
                  <button
                    onClick={() => handleBulkMove(analytics.filesList.filter(f => f.tier === 'warm'), 'archive')}
                    className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded flex items-center gap-1"
                    title="Összes archiválása"
                  >
                    📦 Összes → Archive
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {analytics.filesList
                  .filter(f => f.tier === 'warm')
                  .sort((a, b) => b.ageInDays - a.ageInDays)
                  .map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/20 rounded text-xs gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate font-mono">{file.path}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-gray-700 dark:text-gray-300">{formatFileSize(file.size)}</div>
                        <div className="text-gray-500 dark:text-gray-400">{file.ageInDays.toFixed(0)} nap</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleMoveFile(file.path, 'cold')}
                          disabled={movingFile === file.path}
                          className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded disabled:opacity-50"
                          title="Áthelyezés Cold-ba"
                        >
                          ❄️
                        </button>
                        <button
                          onClick={() => handleMoveFile(file.path, 'archive')}
                          disabled={movingFile === file.path}
                          className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded disabled:opacity-50"
                          title="Archiválás"
                        >
                          📦
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Cold Files */}
          {analytics.filesList.filter(f => f.tier === 'cold').length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-400 flex items-center">
                  <SnowflakeIcon className="w-4 h-4 mr-2" />
                  Cold Storage ({analytics.filesList.filter(f => f.tier === 'cold').length} fájl)
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkMove(analytics.filesList.filter(f => f.tier === 'cold'), 'archive')}
                    className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded flex items-center gap-1"
                    title="Összes archiválása"
                  >
                    📦 Összes → Archive
                  </button>
                  <button
                    onClick={() => handleBulkMove(analytics.filesList.filter(f => f.tier === 'cold'), 'warm')}
                    className="px-2 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded flex items-center gap-1"
                    title="Összes visszahelyezése Warm-ba"
                  >
                    ⬆️ Összes → Warm
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {analytics.filesList
                  .filter(f => f.tier === 'cold')
                  .sort((a, b) => b.ageInDays - a.ageInDays)
                  .map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate font-mono">{file.path}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-gray-700 dark:text-gray-300">{formatFileSize(file.size)}</div>
                        <div className="text-gray-500 dark:text-gray-400">{file.ageInDays.toFixed(0)} nap</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleMoveFile(file.path, 'archive')}
                          disabled={movingFile === file.path}
                          className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded disabled:opacity-50"
                          title="Archiválás"
                        >
                          📦
                        </button>
                        <button
                          onClick={() => handleMoveFile(file.path, 'warm')}
                          disabled={movingFile === file.path}
                          className="px-2 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded disabled:opacity-50"
                          title="Vissza Warm-ba"
                        >
                          ⬆️
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Archive Files */}
          {analytics.filesList.filter(f => f.tier === 'archive').length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-center">
                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                  Archive ({analytics.filesList.filter(f => f.tier === 'archive').length} fájl)
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkMove(analytics.filesList.filter(f => f.tier === 'archive'), 'cold')}
                    className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded flex items-center gap-1"
                    title="Összes visszahelyezése Cold-ba"
                  >
                    ⬆️ Összes → Cold
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {analytics.filesList
                  .filter(f => f.tier === 'archive')
                  .sort((a, b) => b.ageInDays - a.ageInDays)
                  .map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-xs gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate font-mono">{file.path}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-gray-700 dark:text-gray-300">{formatFileSize(file.size)}</div>
                        <div className="text-gray-500 dark:text-gray-400">{file.ageInDays.toFixed(0)} nap</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleMoveFile(file.path, 'cold')}
                          disabled={movingFile === file.path}
                          className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded disabled:opacity-50"
                          title="Vissza Cold-ba"
                        >
                          ⬆️
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Hot Files (for reference) */}
          {analytics.filesList.filter(f => f.tier === 'hot').length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3 flex items-center">
                <FireIcon className="w-4 h-4 mr-2" />
                Hot Storage ({analytics.filesList.filter(f => f.tier === 'hot').length} fájl) - Optimális
              </h4>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Ezek a fájlok az elmúlt 7 napban lettek létrehozva, optimálisan vannak tárolva.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lifecycle Actions */}
      {lifecycleActions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Lifecycle Management Actions
          </h3>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
            <div className="flex">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 dark:text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  {lifecycleActions.length} optimalizálási lehetőség található:
                </p>
                <div className="space-y-1">
                  {lifecycleActions.slice(0, 5).map((action, index) => (
                    <div key={index} className="text-sm text-yellow-700 dark:text-yellow-400">
                      • {action}
                    </div>
                  ))}
                  {lifecycleActions.length > 5 && (
                    <div className="text-sm text-yellow-600 dark:text-yellow-500">
                      ... és még {lifecycleActions.length - 5} további
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <Transition appear show={confirmDialog.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 flex items-center gap-2"
                  >
                    <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />
                    {confirmDialog.title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line">
                      {confirmDialog.message}
                    </p>
                  </div>

                  <div className="mt-4 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                    >
                      Mégse
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={confirmDialog.onConfirm}
                    >
                      Áthelyezés
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Alert Dialog */}
      <Transition appear show={alertDialog.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 flex items-center gap-2"
                  >
                    {alertDialog.type === 'success' ? (
                      <CheckCircleIcon className="w-6 h-6 text-green-500" />
                    ) : (
                      <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
                    )}
                    {alertDialog.title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line">
                      {alertDialog.message}
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default StorageOptimizationDashboard;
